import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgreement } from '../context/AgreementContext';
import { StepWizard } from './StepWizard';
import { PurchaserTypeStep } from './PurchaserTypeStep';
import { PreferencesStep } from './PreferencesStep';
import { ContactInfoStep } from './ContactInfoStep';
import { AgreementPreview } from './AgreementPreview';
import { submitPurchaserDetails, getAgreementById } from '../services/agreementService';
import { Preferences } from '../types';

export function AgreementWizard() {
  const { agreement_id } = useParams<{ agreement_id: string }>();
  const navigate = useNavigate();
  const { state, dispatch } = useAgreement();
  const [currentStep, setCurrentStep] = useState(() => {
    const step = state.currentStep;
    return (typeof step === 'number' && !isNaN(step)) ? step : 1;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [agreementStatus, setAgreementStatus] = useState<string | null>(null);

  // Fetch the latest agreement data when component mounts
  useEffect(() => {
    const fetchAgreement = async () => {
      if (!agreement_id) return;
      
      try {
        setIsLoading(true);
        console.log("Fetching latest agreement data for ID:", agreement_id);
        const data = await getAgreementById(agreement_id);
        console.log("Agreement data received:", data);
        console.log("Agreement purchaser_details:", data.purchaser_details);
        
        // Store the agreement status for later use
        const isReviewed = data.status === "reviewed";
        const isSent = data.status === "sent";
        const hasIndividuals = data.purchaser_details?.individualDetails &&
                              data.purchaser_details.individualDetails.length > 0;
        
        setAgreementStatus(data.status);
        console.log("Agreement status:", data.status, "isReviewed:", isReviewed, "isSent:", isSent);
        console.log("Has individuals:", hasIndividuals, "Count:", data.purchaser_details?.individualDetails?.length || 0);
        
        // If status is "sent", redirect to the WelcomePage
        if (isSent) {
          console.log("Status is sent - redirecting to WelcomePage");
          navigate(`/${agreement_id}`);
          return; // Exit early to prevent further processing
        }
        
        // For signed agreements, we want to show the AgreementPreview step
        const isSigned = data.status === "signed";
        
        // Load agreement data into context
        let hasPreferences = false;
        let preferencesData = null;
        
        if (data.purchaser_details) {
          // Load purchaser type into context
          console.log("DEBUG - purchaser_details:", data.purchaser_details);
          console.log("DEBUG - purchaserType from backend:", data.purchaser_details.purchaserType);
          console.log("DEBUG - typeof purchaserType:", typeof data.purchaser_details.purchaserType);
          console.log("DEBUG - purchaserType === 'personal':", data.purchaser_details.purchaserType === 'personal');
          
          if (data.purchaser_details.purchaserType) {
            console.log("DEBUG - Setting purchaser type in context");
            dispatch({
              type: 'SET_PURCHASER_TYPE',
              payload: data.purchaser_details.purchaserType
            });
            console.log("Loaded purchaser type into context:", data.purchaser_details.purchaserType);
            
            // Force a log of the current state after dispatch
            setTimeout(() => {
              console.log("DEBUG - State after setting purchaser type:", {
                purchaserType: state.purchaserType,
                contextPurchaserType: state.purchaserType === 'personal'
              });
            }, 0);
          } else {
            console.log("DEBUG - No purchaserType in data, using default 'personal'");
            dispatch({
              type: 'SET_PURCHASER_TYPE',
              payload: 'personal'
            });
          }
          
          // Load preferences if available - check both direct and nested locations
          let preferencesData = null;
          if (data.purchaser_details.preferences) {
            preferencesData = data.purchaser_details.preferences;
            console.log("Found preferences in direct location:", preferencesData);
          } else if (data.purchaser_details.agreementDetails?.preferences) {
            preferencesData = data.purchaser_details.agreementDetails.preferences;
            console.log("Found preferences in agreementDetails:", preferencesData);
          }
          
          if (preferencesData) {
            dispatch({
              type: 'SET_PREFERENCES',
              payload: preferencesData
            });
            console.log("Loaded preferences into context:", preferencesData);
            hasPreferences = true;
          } else {
            console.log("No preferences found in backend data");
          }
          
          // Load individual details if available
          if (data.purchaser_details.individualDetails) {
            // Ensure all individualDetails have id, role, and addedAt
            const enhancedIndividualDetails = data.purchaser_details.individualDetails.map((person: any, index: number) => ({
              ...person,
              id: person.id || Date.now().toString() + index,
              role: person.role || 'individual',
              addedAt: person.addedAt || new Date()
            }));
            
            dispatch({
              type: 'SET_INDIVIDUAL_DETAILS',
              payload: enhancedIndividualDetails
            });
            console.log("Loaded individual details into context");
          }
        }
        
        // Load the agreement data into context
        console.log("Data from backend:", data);
        console.log("Current state before loading:", state);
        
        // Create a merged state that prioritizes backend data
        // Use the preferencesData we already extracted above
        const mergedState = {
          ...state,
          purchaserType: data.purchaser_details?.purchaserType || state.purchaserType,
          preferences: preferencesData || state.preferences,
          individualDetails: data.purchaser_details?.individualDetails || state.individualDetails,
          smsfDetails: data.purchaser_details?.smsfDetails || state.smsfDetails,
          trustDetails: data.purchaser_details?.trustDetails || state.trustDetails,
          companyDetails: data.purchaser_details?.companyDetails || state.companyDetails,
          currentStep: state.currentStep,
          furthestStep: state.furthestStep,
          isComplete: data.status === "completed" || state.isComplete,
          total_fee: data.total_fee,
          total_retainer: data.total_retainer
        };
        
        console.log("Fee values from backend:", {
          total_fee: data.total_fee,
          total_retainer: data.total_retainer
        });
        
        console.log("Merged state to load:", mergedState);
        
        dispatch({
          type: 'LOAD_AGREEMENT',
          payload: mergedState
        });
        
        // IMPORTANT: Handle step navigation directly here after all data is loaded
        if (isSigned || hasIndividuals) {
          // For signed agreements or if individualDetails has at least one element, go directly to step 4 (AgreementPreview)
          console.log(`${isSigned ? 'Signed agreement' : 'Individual details found'} - directly setting step to 4 (AgreementPreview)`);
          
          // Update both local state and context
          setCurrentStep(4);
          dispatch({ type: 'SET_CURRENT_STEP', payload: 4 });
          dispatch({ type: 'UPDATE_FURTHEST_STEP', payload: 4 });
        } else if (isReviewed && data.purchaser_details?.purchaserType) {
          // For reviewed agreements with purchaser type set:
          // - If preferences exist, redirect directly to ContactInfoStep (step 3)
          // - Otherwise, go to PreferencesStep (step 2)
          const targetStep = hasPreferences ? 3 : 2;
          
          console.log(`Status is reviewed and purchaser type is set - directly setting step to ${targetStep}`);
          if (hasPreferences) {
            console.log('Preferences exist - redirecting to ContactInfoStep (step 3)');
          } else {
            console.log('No preferences found - redirecting to PreferencesStep (step 2)');
          }
          
          // Update both local state and context
          setCurrentStep(targetStep);
          dispatch({ type: 'SET_CURRENT_STEP', payload: targetStep });
          dispatch({ type: 'UPDATE_FURTHEST_STEP', payload: targetStep });
        }
        
        // Set initialized after all data processing and navigation logic
        setIsInitialized(true);
        console.log("Agreement data initialized");
      } catch (error) {
        console.error("Error fetching agreement:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgreement();
  }, [agreement_id, dispatch]);

  // This effect is no longer needed as we handle navigation in the data loading effect
  // Keeping it for debugging purposes but with modified logic
  useEffect(() => {
    console.log("Debug - Current state:", {
      isLoading,
      isInitialized,
      purchaserType: state.purchaserType,
      agreementStatus,
      currentStep,
      contextStep: state.currentStep
    });
  }, [state.purchaserType, agreementStatus, dispatch, isInitialized, isLoading, currentStep, state.currentStep]);

  // Sync with context when currentStep changes
  useEffect(() => {
    console.log("Sync step useEffect running:", {
      isInitialized,
      contextStep: state.currentStep,
      localStep: currentStep
    });
    
    // Always sync with context step to ensure navigation works in both directions
    // This allows clicking on step icons to navigate between steps
    // IMPORTANT: Only sync if the context step is a valid number
    if (typeof state.currentStep === 'number' && !isNaN(state.currentStep)) {
      setCurrentStep(state.currentStep);
      console.log("Synced currentStep from context:", state.currentStep);
    } else {
      console.log("Context step is invalid, keeping current step:", currentStep);
    }
  }, [state.currentStep, currentStep]);

  // Refetch agreement data when navigating to specific steps that need fresh data
  useEffect(() => {
    const refetchAgreementData = async () => {
      if (!agreement_id || isLoading || !isInitialized) return;
      
      // Refetch data when navigating to PreferencesStep (step 2) to ensure we have latest preferences
      if (currentStep === 2) {
        console.log("Navigating to PreferencesStep - refetching agreement data to ensure latest preferences");
        
        try {
          const data = await getAgreementById(agreement_id);
          console.log("Refetched agreement data for PreferencesStep:", data);
          
          // Update preferences in context if they exist in the backend
          let preferencesData = null;
          if (data.purchaser_details?.preferences) {
            preferencesData = data.purchaser_details.preferences;
            console.log("Found preferences in direct location:", preferencesData);
          } else if (data.purchaser_details?.agreementDetails?.preferences) {
            preferencesData = data.purchaser_details.agreementDetails.preferences;
            console.log("Found preferences in agreementDetails:", preferencesData);
          }
          
          if (preferencesData) {
            dispatch({
              type: 'SET_PREFERENCES',
              payload: preferencesData
            });
            console.log("Updated preferences in context from refetch:", preferencesData);
          } else {
            console.log("No preferences found in refetched data");
          }
        } catch (error) {
          console.error("Error refetching agreement data:", error);
        }
      }
    };
    
    refetchAgreementData();
  }, [currentStep, agreement_id, isLoading, isInitialized, dispatch]);

  // Debug effect to log when currentStep changes
  useEffect(() => {
    console.log("Current step changed to:", currentStep);
  }, [currentStep]);

  const saveDataToBackend = async (step: number, directPreferences?: Preferences) => {
    if (!agreement_id) return;
    
    // Don't save data if agreement is confirmed
    if (agreementStatus === 'confirmed') {
      console.log("Agreement is confirmed - skipping data save to backend");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data based on current step
      let dataToSave: {
        purchaserType?: string;
        purchaserInfo?: any;
        agreementDetails?: any;
        preferences?: {
          maxPrice: number;
          propertyPreferences: string;
        };
        individualDetails?: any[];
        smsfDetails?: any;
        trustDetails?: any;
        companyDetails?: any;
        signatures?: any[];
      } = {};
      
      switch (step) {
        case 1: // Purchaser Type
          dataToSave = {
            purchaserType: state.purchaserType
          };
          console.log("Saving purchaser type data:", dataToSave);
          break;
        case 2: // Preferences
          // Use direct preferences if available, otherwise use state
          const preferencesToUse = directPreferences || state.preferences;
          console.log("Preparing to save preferences data:", preferencesToUse);
          
          // Debug check to ensure preferences are being correctly prepared
          if (preferencesToUse.propertyPreferences === '') {
            console.error("WARNING: propertyPreferences is empty when it should have a value!");
            console.log("Full state:", state);
            console.log("Direct preferences:", directPreferences);
            
            throw new Error("Cannot save empty property preferences");
          }
          
          // Create a clean copy of the preferences
          const cleanPreferences = {
            maxPrice: preferencesToUse.maxPrice,
            propertyPreferences: preferencesToUse.propertyPreferences
          };
          
          // Create agreementDetails object with preferences
          const agreementDetails = {
            preferences: cleanPreferences
          };
          
          dataToSave = {
            purchaserType: state.purchaserType,
            purchaserInfo: {},
            agreementDetails: agreementDetails
          };
          
          console.log("Saving preferences data:", dataToSave);
          break;
        case 3: // Contact Info
          // For step 3, we'll skip saving data here since ContactInfoStep.tsx
          // handles saving the data with the correct structure before calling onNext()
          console.log("Skipping saveDataToBackend for step 3 (Contact Info) - data already saved by ContactInfoStep");
          return;
          
        case 4: // Agreement Preview
          // Create agreementDetails object with preferences and entity-specific details
          let previewAgreementDetails: any = {
            preferences: state.preferences
          };
          
          // Add entity-specific details to agreementDetails
          if (state.purchaserType === 'smsf' && state.smsfDetails) {
            previewAgreementDetails = {
              ...previewAgreementDetails,
              smsfName: state.smsfDetails.smsfName,
              corporateTrustee: state.smsfDetails.corporateTrustee,
              trustName: state.smsfDetails.trustName
            };
          } else if (state.purchaserType === 'trust' && state.trustDetails) {
            previewAgreementDetails = {
              ...previewAgreementDetails,
              trustName: state.trustDetails.trustName
            };
          } else if (state.purchaserType === 'company' && state.companyDetails) {
            previewAgreementDetails = {
              ...previewAgreementDetails,
              trustName: state.companyDetails.trustName,
              corporateTrustee: state.companyDetails.corporateTrustee
            };
          }
          
          // Set individualDetails based on purchaser type
          let previewIndividualDetails = state.individualDetails;
          if (state.purchaserType === 'smsf' && state.smsfDetails) {
            previewIndividualDetails = state.smsfDetails.directors;
          } else if (state.purchaserType === 'trust' && state.trustDetails) {
            previewIndividualDetails = state.trustDetails.directors;
          } else if (state.purchaserType === 'company' && state.companyDetails) {
            previewIndividualDetails = state.companyDetails.directors;
          }
          
          dataToSave = {
            purchaserType: state.purchaserType,
            purchaserInfo: {},
            agreementDetails: previewAgreementDetails,
            individualDetails: previewIndividualDetails,
            signatures: state.signatures
          };
          break;
      }
      
      // Skip if we're returning early (e.g., for step 3)
      if (step === 3) return;
      
      // Submit data to backend
      console.log("Submitting data to backend:", dataToSave);
      console.log("AgreementDetails being sent:", dataToSave.agreementDetails);
      
      const response = await submitPurchaserDetails(agreement_id, dataToSave);
      console.log("Data successfully submitted to backend");
      console.log("Backend response:", response);
      
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToNextStep = async (directPreferences?: Preferences) => {
    const nextStep = currentStep + 1;
    
    console.log("goToNextStep - Current step:", currentStep);
    
    // If we're coming from the preferences step and have direct preferences
    if (currentStep === 2 && directPreferences) {
      console.log("goToNextStep - Received direct preferences:", directPreferences);
      
      // Update the context with the latest preferences
      dispatch({ type: 'SET_PREFERENCES', payload: directPreferences });
      
      // Wait a moment for the state to update
      await new Promise(resolve => setTimeout(resolve, 0));
      
      console.log("goToNextStep - Updated context with direct preferences");
    }
    
    // Add additional logging to track state preservation
    console.log("goToNextStep - State preservation check:", {
      purchaserType: state.purchaserType,
      hasPreferences: !!state.preferences?.propertyPreferences,
      hasIndividualDetails: state.individualDetails?.length > 0,
      hasSMSFDetails: !!state.smsfDetails,
      hasTrustDetails: !!state.trustDetails,
      hasCompanyDetails: !!state.companyDetails
    });
    
    console.log("goToNextStep - Current state before saving:", {
      purchaserType: state.purchaserType,
      preferences: state.preferences,
      individualDetails: state.individualDetails,
      currentStep: state.currentStep
    });
    
    // Save data to backend before proceeding
    await saveDataToBackend(currentStep, directPreferences);
    
    console.log("goToNextStep - After saving to backend, before updating step");
    
    // Update state
    setCurrentStep(nextStep);
    dispatch({ type: 'SET_CURRENT_STEP', payload: nextStep });
    dispatch({ type: 'UPDATE_FURTHEST_STEP', payload: nextStep });
    
    console.log("goToNextStep - Updated to step:", nextStep);
  };

  const goToPreviousStep = () => {
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    dispatch({ type: 'SET_CURRENT_STEP', payload: prevStep });
    // Don't update furthest step when going back
  };

  const renderCurrentStep = () => {
    // Show loading state while fetching agreement data
    if (isLoading) {
      return (
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading agreement data...</p>
        </div>
      );
    }
    
    console.log("Rendering step:", currentStep);
    
    // Force the component to use the latest step value
    const stepToRender = currentStep;
    
    switch (stepToRender) {
      case 1:
        return <PurchaserTypeStep onNext={goToNextStep} agreementStatus={agreementStatus} />;
      case 2:
        return <PreferencesStep onNext={(preferences) => goToNextStep(preferences)} onBack={goToPreviousStep} agreementStatus={agreementStatus} />;
      case 3:
        return <ContactInfoStep onNext={goToNextStep} onBack={goToPreviousStep} agreementStatus={agreementStatus} />;
      case 4:
        return <AgreementPreview onBack={goToPreviousStep} />;
      default:
        return <PurchaserTypeStep onNext={goToNextStep} agreementStatus={agreementStatus} />;
    }
  };

  return (
    <StepWizard>
      {renderCurrentStep()}
    </StepWizard>
  );
}